import classNames from "classnames";
import Fuse from "fuse.js";
import { isEmpty } from "lodash";
import React, { useEffect, useState, useRef } from "react";
import { navigate, Link } from "gatsby";
import { trackSearchQuery, trackSearchResultClick, trackSearchNoResults } from "../../common/utils/analytics";
import "./SearchBar.scss";

export default function SearchBar(props) {
    const { searchData, searchResultPostFix, placeholder, locale } = props;
    const prefix = locale ? `/${locale}` : "";
    const [searchQuery, setSearchQuery] = useState(props.initialQuery || "");
    const [isInputFocused, setIsInputFocused] = useState(props.initialQuery ? true : false);

    useEffect(() => {
        if (props.initialQuery) {
            setSearchQuery(props.initialQuery);
            setIsInputFocused(true);
        }
    }, [props.initialQuery]);
    const [searchResults, setSearchResults] = useState(undefined);
    const [localeSearchData, setLocaleSearchData] = useState(null);
    const searchResultsNotEmpty = !isEmpty(searchResults);
    const searchWrapperRef = useRef(null);

    // Fetch locale-specific search data on mount
    useEffect(() => {
        if (!locale) return;
        const type = searchResultPostFix === "chord" ? "chords" : "scales";
        fetch(`/searchData/${type}.${locale}.json`)
            .then(res => res.ok ? res.json() : null)
            .then(data => { if (data) setLocaleSearchData(data); })
            .catch(() => {});
    }, [locale, searchResultPostFix]);

    const activeData = localeSearchData || searchData;

    const classes = classNames(
        "search-bar-container",
        isInputFocused && "focused",
        searchResultsNotEmpty && isInputFocused && "has-results",
    )
    const options = {
        shouldSort: true,
        caseSensitive: false,
        threshold: 0.05,
        location: 0,
        distance: 100,
        maxPatternLength: 32,
        minMatchCharLength: 1,
        keys: localeSearchData
            ? ["a", "e"]
            : ["a"],
    };

    const optionsReverse = {
        shouldSort: true,
        caseSensitive: false,
        threshold: 0.5,
        location: 0,
        distance: 100,
        maxPatternLength: 32,
        minMatchCharLength: 1,
        keys: [
            {
                name: "c",
                weight: 1,
            },
        ]
    }

    const fuse = new Fuse(activeData, options);
    const fuseReverseSearch = new Fuse(activeData, optionsReverse)

    const handleClickOutside = event => {
        if (searchWrapperRef.current && !searchWrapperRef.current.contains(event.target)) {
            setIsInputFocused(false)
        }
    }

    useEffect(() => {
        const isReverseSearch = searchQuery.includes(",");
        const fuseObject = isReverseSearch ? fuseReverseSearch : fuse;
        const allResults = fuseObject.search(
            searchQuery.replace(" chord", "")
                .replace("chord", "")
        )

        const topTenSearchResults = isReverseSearch ?
            allResults.filter(chord => !chord.d)
                .slice(0, 10) :
            allResults.slice(0, 10);
        setSearchResults(topTenSearchResults)

        if (searchQuery.length >= 3 && topTenSearchResults.length > 0) {
            trackSearchQuery(searchResultPostFix, searchQuery)
        }

        if (searchQuery.length >= 3 && topTenSearchResults.length === 0) {
            trackSearchNoResults(searchResultPostFix, searchQuery)
        }


        // Bind the event listener
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            // Unbind the event listener on clean up
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [searchQuery])


    return (
        <div className={classes} ref={searchWrapperRef}>
            <div className="search-bar">
                <svg focusable="false" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                    <path style={{ fill: "#666" }} d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"></path>
                </svg>
                <input
                    type="text"
                    value={searchQuery}
                    onChange={event => setSearchQuery(event.target.value)}
                    placeholder={placeholder || `Type here to search for ${searchResultPostFix}s...`}
                    onFocus={() => setIsInputFocused(true)}
                    onKeyDown={e => {
                        if (searchResultsNotEmpty && e.keyCode === 13) {
                            trackSearchResultClick(searchResultPostFix, searchResults[0].a)
                            navigate(`${prefix}${searchResults[0].b}`)
                        }
                    }}
                />
            </div>
            {
                searchResultsNotEmpty && isInputFocused &&
                <div className="search-results-container">
                    <div className="line-break" />
                    <div className="search-results">
                        {
                            searchResults.map(chord => {
                                const reverseSearchString = searchQuery.includes(",") &&
                                    chord.c.split(", ")
                                        .map((note, index) => (
                                            searchQuery.toLowerCase().includes(note.toLowerCase()) ?
                                            <b>{`${index > 0 ? ", " : ""}${note}`}</b>  :
                                            <span>{`${index > 0 ? ", " : ""}${note}`}</span>
                                        ));
                                return (
                                    <Link to={`${prefix}${chord.b}`} key={chord.b} onClick={() => trackSearchResultClick(searchResultPostFix, chord.a)}>

                                        <div className="search-result">
                                            <svg focusable="false" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                                                <path style={{ fill: "#666" }} d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"></path>
                                            </svg>
                                            <p>{(chord.e || chord.a).trim()} {!(chord.e || chord.a).includes(searchResultPostFix) && searchResultPostFix || ""} {reverseSearchString && "("}{reverseSearchString && reverseSearchString}{reverseSearchString && ")"}</p>
                                        </div>
                                    </Link>

                                )  
                            })
                        }
                    </div>
                </div>
            }

        </div>
    )
}